/* @moldura padrao */
/**
 * form-campo.tsx — Lançar despesa EM CAMPO. Rota `/despesas/nova`.
 *
 * A porta de entrada é o atalho "Despesa" no rodapé do app instalado: a pessoa
 * está na rua, com a nota na mão, e não passa por lista nenhuma — abriu, lançou,
 * fechou. Por isso: coluna única, um cartão por assunto, controles do tamanho do
 * dedo, e o que é exceção (descrição longa, vínculo de OS fora do campo) nasce
 * recolhido atrás de um link.
 *
 * A REGRA não mora aqui: está em `useDespesaForm`. Esta tela só desenha. O
 * arranjo de escritório da mesma coisa é `form-completo.tsx`.
 */
import { CustomBotaoDitado } from '@/components/CustomBotaoDitado';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { CustomCampoData } from '@/components/CustomCamposData';
import { useDespesaForm } from './useDespesaForm';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

export function DespesaFormCampoPage() {
  const { id } = useParams();
  const f = useDespesaForm(id);

  // Exceções nascem recolhidas: no celular, cada campo a mais é uma rolagem.
  const [mostrarOS, setMostrarOS] = useState(f.contexto === 'campo');
  const [mostrarDescricao, setMostrarDescricao] = useState(false);

  if (f.carregando) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }

  return (
    <CustomMolduraPagina titulo="Nova despesa" semCaminho>
      {/* A COLUNA DE 560px É DESTA TELA, não da moldura. É formulário de campo,
          preenchido em pé na casa do cliente: linha curta é o que faz o dedo
          acertar. Não virou propriedade porque é um uso só — o gatilho para
          virar peça é o SEGUNDO. */}
      <div className="mx-auto w-100" style={{ maxWidth: 560 }}>

        <form onSubmit={f.salvar} className="d-flex flex-column gap-3">
          {/* 1. Quanto e quando — o que a pessoa já sabe ao abrir a tela. */}
          <div className="card adminuiux-card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-sm-7">
                  <CustomCampoNumeroMoeda
                    lg
                    label="Valor"
                    id="despesa-valor"
                    valor={f.form.valor}
                    onChange={(v) => f.setCampo('valor', v)}
                  />
                </div>
                <div className="col-12 col-sm-5">
                  {/* Data Rápida: digitar 1108 vira 11/08. */}
                  <CustomCampoData
                    label="Data"
                    id="despesa-data"
                    valor={f.form.data}
                    onChange={(iso) => f.setCampo('data', iso)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 2. O que foi. */}
          <div className="card adminuiux-card">
            <div className="card-body">
              <div className="form-floating">
                <select
                  id="despesa-categoria"
                  className="form-select"
                  value={f.form.categoria_id}
                  onChange={(e) => f.setCampo('categoria_id', e.target.value)}
                >
                  <option value="">Selecione…</option>
                  {f.categorias.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nome}
                    </option>
                  ))}
                </select>
                <label htmlFor="despesa-categoria">Categoria</label>
              </div>
              {/* O "quando usar esta e não outra" mora no banco e aparece aqui,
                  na hora exata da dúvida. */}
              {f.categoriaEscolhida?.criterio && (
                <div className="form-text">{f.categoriaEscolhida.criterio}</div>
              )}

              {/* O MICROFONE (26/09): em campo, falar é mais fácil que digitar. */}
              <div className="input-group mt-3">
                <div className="form-floating">
                  <input
                    id="despesa-historico"
                    className="form-control"
                    maxLength={120}
                    placeholder=" "
                    value={f.form.historico}
                    onChange={(e) => f.setCampo('historico', e.target.value)}
                  />
                  <label htmlFor="despesa-historico">
                    O que foi? (ex.: Pedágio - Rio)
                  </label>
                </div>
                <CustomBotaoDitado
                  texto={f.form.historico}
                  aoMudar={(v) => f.setCampo('historico', v.slice(0, 120))}
                />
              </div>

              {mostrarDescricao ? (
                <div className="input-group mt-3">
                  <div className="form-floating">
                    <textarea
                      id="despesa-descricao"
                      className="form-control"
                      style={{ height: 90 }}
                      placeholder=" "
                      value={f.form.descricao}
                      onChange={(e) => f.setCampo('descricao', e.target.value)}
                    />
                    <label htmlFor="despesa-descricao">Descrição</label>
                  </div>
                  <CustomBotaoDitado
                    texto={f.form.descricao}
                    aoMudar={(v) => f.setCampo('descricao', v)}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none mt-2"
                  onClick={() => setMostrarDescricao(true)}
                >
                  <i className="bi bi-plus-lg me-1"></i> Explicar melhor
                </button>
              )}
            </div>
          </div>

          {/* 3. Como pagou — três alvos de toque valem mais que um select. */}
          <div className="card adminuiux-card">
            <div className="card-body">
              <label className="form-label fw-medium">Como pagou?</label>
              <div className="d-flex flex-column gap-2">
                {f.formas.map((fp) => (
                  <button
                    key={fp.codigo}
                    type="button"
                    className={`btn text-start ${
                      f.form.forma_pgto === fp.codigo
                        ? 'btn-theme'
                        : 'btn-outline-theme'
                    }`}
                    onClick={() => f.escolherForma(fp.codigo)}
                  >
                    <div className="fw-medium">{fp.nome}</div>
                    {fp.descricao && (
                      <div className="small opacity-75">{fp.descricao}</div>
                    )}
                  </button>
                ))}
              </div>

              {/* Cartão de qual loja? — só quando a forma pede (Cartão da loja). */}
              {f.pedeLoja && (
                <div className="mt-3">
                  <label className="form-label fw-medium">
                    Cartão de qual loja?
                  </label>
                  <div className="d-flex gap-2">
                    {f.lojas.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        className={`btn flex-fill ${
                          f.form.loja_cartao_id === String(l.id)
                            ? 'btn-theme'
                            : 'btn-outline-theme'
                        }`}
                        onClick={() => f.setCampo('loja_cartao_id', String(l.id))}
                      >
                        {f.rotuloLoja(l)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {f.formaEscolhida?.ressarcivel && (
                <div className="alert alert-warning small mt-2 mb-0 py-2">
                  <i className="bi bi-info-circle me-1"></i>
                  Fica registrada para <strong>restituição</strong> a você.
                </div>
              )}
            </div>
          </div>

          {/* 4. Comprovante — direto da câmera. */}
          <div className="card adminuiux-card">
            <div className="card-body">
              <label className="form-label fw-medium">Comprovante</label>
              {f.form.comprovante ? (
                <div className="position-relative">
                  <img
                    src={`/uploads/${f.form.comprovante}`}
                    alt="Comprovante"
                    className="img-fluid rounded"
                    style={{ maxHeight: 240, width: '100%', objectFit: 'cover' }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-theme text-danger position-absolute top-0 end-0 m-2"
                    onClick={f.removerComprovante}
                    aria-label="Remover comprovante"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-lg btn-outline-theme w-100"
                  onClick={() => f.fileRef.current?.click()}
                  disabled={f.enviandoFoto}
                >
                  {f.enviandoFoto ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-camera me-2"></i> Tirar foto / anexar
                    </>
                  )}
                </button>
              )}
              <input
                ref={f.fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="d-none"
                onChange={f.onArquivo}
              />
            </div>
          </div>

          {/* 5. A que pertence. */}
          <div className="card adminuiux-card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="form-floating">
                    <select
                      id="despesa-viagem"
                      className="form-select"
                      value={f.form.viagem_id}
                      onChange={(e) => f.escolherViagem(e.target.value)}
                      disabled={f.semViagem}
                    >
                      <option value="">{f.semViagem ? '—' : 'Escolha a viagem'}</option>
                      {f.viagens.map((v) => (
                        <option key={v.id} value={String(v.id)}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="despesa-viagem">Está numa viagem?</label>
                  </div>
                  {/* Ou a viagem, ou isto — um dos dois (dele, 26/09). */}
                  <div className="form-check mt-2 mb-0">
                    <input
                      id="despesa-sem-viagem"
                      type="checkbox"
                      className="form-check-input"
                      checked={f.semViagem}
                      onChange={(e) => f.marcarSemViagem(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="despesa-sem-viagem">
                      Sem viagem
                    </label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-floating">
                    <select
                      id="despesa-loja"
                      className="form-select"
                      value={f.form.loja_id}
                      onChange={(e) => f.setCampo('loja_id', e.target.value)}
                    >
                      <option value="">—</option>
                      {f.lojas.map((l) => (
                        <option key={l.id} value={String(l.id)}>
                          {f.rotuloLoja(l)}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="despesa-loja">Loja</label>
                  </div>
                </div>

                {/* A OS é do banco da DESPESA, não da viagem: existe para todos,
                    mas só nasce aberta para quem instala. */}
                {mostrarOS ? (
                  <div className="col-12">
                    <div className="form-floating">
                      <select
                        id="despesa-os"
                        className="form-select"
                        value={f.form.os_id}
                        onChange={(e) => f.setCampo('os_id', e.target.value)}
                      >
                        <option value="">—</option>
                        {f.osLista.map((o) => (
                          <option key={o.id} value={String(o.id)}>
                            {o.codigo}
                            {o.cliente_nome ? ` — ${o.cliente_nome}` : ''}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="despesa-os">Ordem de serviço</label>
                    </div>
                  </div>
                ) : (
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => setMostrarOS(true)}
                    >
                      <i className="bi bi-link-45deg me-1"></i>
                      Vincular a uma ordem de serviço
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* `mb-5` não é enfeite: no celular o teclado sobe e comeria o botão
              se ele encostasse no fim da página. */}
          <div className="d-grid mb-5">
            <button
              type="submit"
              className="btn btn-lg btn-theme"
              disabled={f.salvando || f.enviandoFoto}
            >
              {f.salvando && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              Lançar despesa
            </button>
          </div>
        </form>
      </div>
    </CustomMolduraPagina>
  );
}
